export type Direction = "u" | "d" | "l" | "r";

export interface Cell {
  row: number;
  col: number;
}

export interface MazeCell {
  u: boolean;
  d: boolean;
  l: boolean;
  r: boolean;
  visited: boolean;
}

export interface WallLine {
  key: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface MazeBlueprint {
  rows: number;
  cols: number;
  grid: MazeCell[][];
  entryCell: Cell;
  exitCell: Cell;
  wallLines: WallLine[];
  seed: string;
}

interface MazeGenerationOptions {
  rows: number;
  cols: number;
  seed: string;
}

const DIRECTION_ORDER: Direction[] = ["u", "d", "l", "r"];

const DIRECTION_DELTAS: Record<
  Direction,
  { rowDelta: number; colDelta: number; opposite: Direction }
> = {
  u: { rowDelta: -1, colDelta: 0, opposite: "d" },
  d: { rowDelta: 1, colDelta: 0, opposite: "u" },
  l: { rowDelta: 0, colDelta: -1, opposite: "r" },
  r: { rowDelta: 0, colDelta: 1, opposite: "l" },
};

function hashStringToSeedNumber(value: string) {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function createSeededRandom(seedText: string) {
  let state = hashStringToSeedNumber(seedText) || 0x12345678;

  return () => {
    state += 0x6d2b79f5;
    let temp = state;
    temp = Math.imul(temp ^ (temp >>> 15), temp | 1);
    temp ^= temp + Math.imul(temp ^ (temp >>> 7), temp | 61);
    return ((temp ^ (temp >>> 14)) >>> 0) / 4294967296;
  };
}

function createMazeGrid(rows: number, cols: number) {
  return Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => ({
      u: false,
      d: false,
      l: false,
      r: false,
      visited: false,
    })),
  );
}

function shuffledDirections(random: () => number) {
  const directions = [...DIRECTION_ORDER];

  for (let index = directions.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [directions[index], directions[swapIndex]] = [
      directions[swapIndex],
      directions[index],
    ];
  }

  return directions;
}

function carveMaze(
  grid: MazeCell[][],
  currentRow: number,
  currentCol: number,
  rows: number,
  cols: number,
  random: () => number,
) {
  grid[currentRow][currentCol].visited = true;

  const directions = shuffledDirections(random);

  for (const direction of directions) {
    const delta = DIRECTION_DELTAS[direction];
    const nextRow = currentRow + delta.rowDelta;
    const nextCol = currentCol + delta.colDelta;

    if (nextRow < 0 || nextRow >= rows || nextCol < 0 || nextCol >= cols) {
      continue;
    }

    if (grid[nextRow][nextCol].visited) {
      continue;
    }

    grid[currentRow][currentCol][direction] = true;
    grid[nextRow][nextCol][delta.opposite] = true;
    carveMaze(grid, nextRow, nextCol, rows, cols, random);
  }
}

function buildWallLines(
  grid: MazeCell[][],
  rows: number,
  cols: number,
  entryCell: Cell,
  exitCell: Cell,
) {
  const lines: WallLine[] = [];

  if (entryCell.col > 0) {
    lines.push({
      key: "outer-top-left",
      x1: 0,
      y1: 0,
      x2: entryCell.col,
      y2: 0,
    });
  }

  if (entryCell.col + 1 < cols) {
    lines.push({
      key: "outer-top-right",
      x1: entryCell.col + 1,
      y1: 0,
      x2: cols,
      y2: 0,
    });
  }

  if (exitCell.col > 0) {
    lines.push({
      key: "outer-bottom-left",
      x1: 0,
      y1: rows,
      x2: exitCell.col,
      y2: rows,
    });
  }

  if (exitCell.col + 1 < cols) {
    lines.push({
      key: "outer-bottom-right",
      x1: exitCell.col + 1,
      y1: rows,
      x2: cols,
      y2: rows,
    });
  }

  lines.push({ key: "outer-left", x1: 0, y1: 0, x2: 0, y2: rows });
  lines.push({ key: "outer-right", x1: cols, y1: 0, x2: cols, y2: rows });

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const mazeCell = grid[row][col];

      if (!mazeCell.r && col < cols - 1) {
        lines.push({
          key: `vr-${row}-${col}`,
          x1: col + 1,
          y1: row,
          x2: col + 1,
          y2: row + 1,
        });
      }

      if (!mazeCell.d && row < rows - 1) {
        lines.push({
          key: `hb-${row}-${col}`,
          x1: col,
          y1: row + 1,
          x2: col + 1,
          y2: row + 1,
        });
      }
    }
  }

  return lines;
}

export function createMazeBlueprint({
  rows,
  cols,
  seed,
}: MazeGenerationOptions): MazeBlueprint {
  const random = createSeededRandom(seed);
  const grid = createMazeGrid(rows, cols);
  carveMaze(grid, 0, 0, rows, cols, random);

  const entryCol = Math.floor(random() * cols);
  const exitCol = cols - 1 - entryCol;

  const entryCell: Cell = { row: 0, col: entryCol };
  const exitCell: Cell = { row: rows - 1, col: exitCol };

  const wallLines = buildWallLines(grid, rows, cols, entryCell, exitCell);

  return {
    rows,
    cols,
    grid,
    entryCell,
    exitCell,
    wallLines,
    seed,
  };
}

export function createMazeSeed() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function exportMazeBlueprint(blueprint: MazeBlueprint) {
  return JSON.stringify(blueprint);
}

export function importMazeBlueprint(serializedBlueprint: string) {
  return JSON.parse(serializedBlueprint) as MazeBlueprint;
}
